var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useAppSelector } from 'hooks/reduxHooks';
import clsx from 'clsx';
import { Box, Typography, Button } from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { InputFields } from 'common/InputFields';
import SortIcon from 'assets/icons/SortIcon.svg';
import { wellnessRightPanelStyle } from './WellnessRightPanel.style';
var SPECIAL_CHAR_REGEX = /^[a-zA-Z0-9\s.]*$/;
var SPECIAL_CHAR_MESSAGE = 'Special characters are not allowed';
var WellnessRightPanel = React.memo(function (_a) {
    var name = _a.name, item = _a.item, updateItem = _a.updateItem, cardTitle = _a.cardTitle, buttonLabel = _a.buttonLabel, inputPlaceholder = _a.inputPlaceholder, readOnly = _a.readOnly;
    var classes = wellnessRightPanelStyle().classes;
    var isLatestVersion = useAppSelector(function (state) { return state.wellnessPlan; }).isLatestVersion;
    var memberPriorityRef = React.useRef(null);
    var challengesRef = React.useRef(null);
    var generateRef = function () {
        switch (name) {
            case 'memberPriority':
                return memberPriorityRef;
            case 'challenges':
                return challengesRef;
            default:
                return null;
        }
    };
    var handleChange = function (seq, e) {
        var value = e.target.value;
        var hasInvalidCharacters = !!value && !SPECIAL_CHAR_REGEX.test(value);
        updateItem(__spreadArray([], item, true).map(function (data) {
            if (data.seq === seq) {
                return {
                    seq: seq,
                    value: value,
                    error: hasInvalidCharacters,
                    errorText: hasInvalidCharacters ? SPECIAL_CHAR_MESSAGE : '',
                };
            }
            else
                return data;
        }));
    };
    var handleAddItem = function () {
        updateItem(function (prevState) { return __spreadArray(__spreadArray([], prevState, true), [
            {
                seq: prevState.length + 1,
                value: '',
                error: false,
                errorText: '',
            },
        ], false); });
        setTimeout(function () {
            if (name == 'memberPriority') {
                memberPriorityRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                });
            }
            else if (name == 'challenges') {
                challengesRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'start',
                });
            }
        }, 100);
    };
    var handleRemoveItem = function (seq) {
        if (readOnly || !isLatestVersion)
            return;
        var filteredArray = item.filter(function (filterData) { return filterData.seq !== seq; });
        updateItem(__spreadArray([], filteredArray, true).map(function (data, index) {
            return __assign(__assign({}, data), { seq: index + 1 });
        }));
    };
    var handleDragStart = function (e, index) {
        if (readOnly)
            return;
        e.dataTransfer.setData('text/plain', index.toString());
    };
    var handleDragOver = function (e) {
        if (readOnly)
            return;
        e.preventDefault();
    };
    var handleDrop = function (e, dropIndex) {
        if (readOnly)
            return;
        e.preventDefault();
        var draggedIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
        if (isNaN(draggedIndex))
            return;
        var updatedItems = __spreadArray([], item, true);
        var draggedItem = updatedItems.splice(draggedIndex, 1)[0];
        updatedItems.splice(dropIndex, 0, draggedItem);
        var reorderedItems = updatedItems.map(function (data, idx) { return (__assign(__assign({}, data), { seq: idx + 1 })); });
        updateItem(reorderedItems);
    };
    return (_jsx(Box, { display: 'flex', flexDirection: 'column', style: { width: '100%', height: '50%' }, "data-testid": 'wellnessRightPanel', children: _jsxs(Box, { className: classes.rightSideFieldsContainer, height: '100%', children: [_jsxs(Box, { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap', children: [_jsxs(Typography, { component: "div", className: classes.fieldsHeading, variant: 'h6', style: { fontWeight: 500 }, children: [cardTitle, _jsx(Box, { component: 'span', className: classes.errorText, children: "*" })] }), !readOnly && _jsx(Button, { variant: 'contained', color: 'primary', size: 'large', disabled: !isLatestVersion, onClick: handleAddItem, children: buttonLabel })] }), _jsx(Box, { className: classes.itemContainer, children: item.map(function (itemData, index) {
                        var _a;
                        return (_jsxs("div", { className: classes.itemData, ref: item.length === index + 1 ? generateRef() : null, draggable: isLatestVersion, onDragStart: function (e) { return handleDragStart(e, index); }, onDragOver: handleDragOver, onDrop: function (e) { return handleDrop(e, index); }, children: [name === 'memberPriority' && item.length !== 1 && (_jsx("img", { src: SortIcon, style: { marginRight: 10 } })), _jsx(InputFields, { isError: itemData.error, errorText: itemData.errorText, eventProps: {
                                        onChange: function (e) { return handleChange(itemData.seq, e); },
                                        value: itemData.value || '',
                                        disabled: readOnly || !isLatestVersion,
                                    }, inputProps: {
                                        name: "".concat(name, "-").concat(itemData.seq),
                                        placeholder: inputPlaceholder,
                                        required: true,
                                        maxLength: 250,
                                        style: {
                                            padding: 0,
                                            height: 55,
                                            boxSizing: 'border-box',
                                        },
                                    }, multiline: true, rows: 2 }), _jsx(HighlightOffIcon, { onClick: function () { return handleRemoveItem(itemData.seq); }, "data-testid": 'handleRemove', className: clsx((_a = {},
                                        _a[classes.removeIcon] = true,
                                        _a[classes.removeIconDisable] = item.length == 1 || readOnly || !isLatestVersion,
                                        _a)) })] }, "".concat(name, "-").concat(itemData.seq)));
                    }) })] }) }));
});
export default WellnessRightPanel;
